/** Reviews (S40, S41), review requests (S42 to S44) and the texting status. docs/50 §4.4, docs/52 §3.5. */
import { z } from 'zod';
import { Cursor, Email, HttpsUrl, IsoDateTime, Limit, PhoneE164, PhoneInput, text } from '../common.ts';
import { Channel, DB_ENUMS, Language, MessagingProfileStatus, ReplyState, ReviewRequestEntry, ReviewRequestSource, ReviewRequestStatus } from '../enums.ts';
import { LeadId, LocationId, ReviewId, ReviewRequestId, UploadId, UserId } from '../ids.ts';

// ---------------------------------------------------------------------------------------------- messaging status

/** The three texting status lines on S40, plus email-only by choice (docs/52 §3.5). */
export const MessagingStatus = z.object({
  state: z.enum(['texts_and_email', 'email_only_pending', 'action_needed', 'email_only']),
  profileStatus: MessagingProfileStatus,
  line: z.string().max(200),
  phoneE164: PhoneE164.nullable(),
  action: z.object({ kind: z.enum(['fix_legal_name', 'add_privacy_page', 'contact_us']), label: z.string().max(40) }).nullable(),
  locationId: LocationId.nullable(),
});

// ---------------------------------------------------------------------------------------------- reviews

export const ReviewSummary = z.object({
  id: ReviewId,
  locationId: LocationId,
  rating: z.number().int().min(1).max(5),
  authorDisplay: z.string().max(120).nullable(),
  comment: z.string().max(5000).nullable(),
  createdTime: IsoDateTime,
  replyState: ReplyState,
  googleUrl: HttpsUrl.nullable(),
  needsYou: z.boolean(),
});

export const ReviewListQuery = z
  .object({
    location: LocationId.optional(),
    filter: z.enum(['all', 'needs_you', 'low']).optional(),
    cursor: Cursor.optional(),
    limit: Limit.optional(),
  })
  .strict();

export const ReviewList = z.object({
  messaging: MessagingStatus,
  pace: z.object({
    thisWeek: z.number().int().min(0),
    topThreeWeeklyAverage: z.number().min(0).nullable(),
    asOf: IsoDateTime.nullable(),
  }),
  items: z.array(ReviewSummary),
  nextCursor: Cursor.nullable(),
});

export const ReviewDetail = ReviewSummary.extend({
  reply: z
    .object({ text: z.string().max(4096), state: ReplyState, postedAt: IsoDateTime.nullable(), draftedBy: z.enum(['us', 'you']) })
    .nullable(),
  sensitive: z.boolean(),
  canPost: z.boolean(),
});

export const ReviewReplyBody = z.discriminatedUnion('action', [
  z.object({ action: z.literal('post') }).strict(),
  z.object({ action: z.literal('edit'), text: text(4096) }).strict(),
  z.object({ action: z.literal('call_me'), note: text(500).optional() }).strict(),
  z.object({ action: z.literal('not_replying') }).strict(),
]);

// ---------------------------------------------------------------------------------------------- review requests

/** The attestation wording shown in full and its version (docs/50 §4.4.3). */
export const Attestation = z
  .object({ accepted: z.literal(true), version: z.string().regex(/^[a-z0-9._-]{1,40}$/).meta({ examples: ['rr-attest-2026-09'] }) })
  .strict();

/**
 * The rules every way of asking shares (docs/53 §10, docs/50 §4.4.2), in one place so the app, the sender and the
 * docs can't drift: how many asks a business may make and send in a day, how long before the same customer may be
 * asked again, how many emails go in one paste and how many rows one import of today's jobs may carry.
 */
export const REVIEW_ASK_LIMITS = {
  /** Asks one business may create in a day, across every way of asking. */
  createdPerDay: 50,
  /** Emails one business may send in a day (first asks and reminders together). */
  sentPerDay: 100,
  /** The same email isn't asked again inside this many days. */
  repeatDays: 365,
  /** Emails in one paste. */
  pasteMax: 20,
  /** Rows in one import of today's jobs. */
  importMax: 50,
  /** Rows the checker reads before it stops. */
  checkMax: 200,
  /** The reminder goes this many days after the first email, once. */
  reminderDays: 3,
  /** How long a new ask waits so Undo works. */
  undoSeconds: 15,
} as const;

export const ReviewRequestCreateBody = z
  .object({
    /** Optional since c133: an ask needs only an email. Without a name the email opens "Hi there". */
    firstName: text(60).optional(),
    /** Email only (c072). A mobile is accepted for old clients of this route and never stored or texted. */
    phone: PhoneInput.optional(),
    email: Email.optional(),
    /** Where the ask was typed: the full form or the quick card on Home. Leads are marked by `leadId`. */
    entry: z.enum(['form', 'quick']).optional(),
    serviceSlug: z.string().max(64).optional(),
    leadId: LeadId.optional(),
    locationId: LocationId.optional(),
    language: Language.optional(),
    attestation: Attestation,
    /** Client-generated key so a retried send never makes two requests. */
    idempotencyKey: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/),
  })
  .strict()
  .refine((b) => b.phone !== undefined || b.email !== undefined, { message: 'a mobile or an email is needed', path: ['phone'] });

export const ReviewRequestView = z.object({
  id: ReviewRequestId,
  locationId: LocationId,
  source: ReviewRequestSource,
  status: ReviewRequestStatus,
  /** null once the contact fields are purged at 90 days (docs/53 §9). */
  firstName: z.string().max(60).nullable(),
  contactLabel: z.string().max(80).nullable(),
  contactPurged: z.boolean(),
  firstChannel: Channel.nullable(),
  scheduledFor: IsoDateTime.nullable(),
  firstAt: IsoDateTime.nullable(),
  reminderAt: IsoDateTime.nullable(),
  openedAt: IsoDateTime.nullable(),
  clickedAt: IsoDateTime.nullable(),
  optedOut: z.boolean(),
  createdBy: UserId.nullable(),
  createdAt: IsoDateTime,
  error: z.string().max(200).nullable(),
  /** How the customer got into the ask (c133). Null on rows from before the review engine. */
  entry: ReviewRequestEntry.nullable().optional(),
});

export const ReviewRequestCreated = z.object({
  request: ReviewRequestView,
  /** Undo shows for 10 seconds (docs/52 S42). */
  undoUntil: IsoDateTime,
});

export const ReviewRequestListQuery = z
  .object({
    status: ReviewRequestStatus.optional(),
    location: LocationId.optional(),
    mine: z.enum(['0', '1']).optional(),
    cursor: Cursor.optional(),
    limit: Limit.optional(),
  })
  .strict();

export const ReviewRequestList = z.object({ items: z.array(ReviewRequestView), nextCursor: Cursor.nullable() });

// ---------------------------------------------------------------------------------------------- review ask templates

/**
 * The review ask emails, written for each business and editable by the owner (deck ruling c131). The words are
 * theirs; the review button, the business address and the unsubscribe line are added by the sender and can't be
 * edited out (CAN-SPAM, docs/50 §4.4.2). Placeholders: {first_name}, {business}, {owner}.
 */
export const ReviewTemplateKind = z.enum(DB_ENUMS['review_templates.kind']);
export const REVIEW_TEMPLATE_PLACEHOLDERS = ['{first_name}', '{business}', '{owner}'] as const;

export const ReviewTemplate = z.object({
  kind: ReviewTemplateKind,
  subject: z.string().max(120),
  body: z.string().max(1200),
  /** False while it's still the template we wrote for them. */
  edited: z.boolean(),
  updatedAt: IsoDateTime.nullable(),
  /** The email as a customer named Maria would read it, with the button line and footer. */
  preview: z.object({ subject: z.string().max(200), text: z.string().max(3000) }),
});

export const ReviewTemplates = z.object({ items: z.array(ReviewTemplate), placeholders: z.array(z.string().max(20)) });

export const ReviewTemplateBody = z.discriminatedUnion('action', [
  z.object({ action: z.literal('save'), kind: ReviewTemplateKind, subject: text(120, 3), body: text(1200, 10) }).strict(),
  z.object({ action: z.literal('reset'), kind: ReviewTemplateKind }).strict(),
]);

export const BulkPreviewBody = z.discriminatedUnion('format', [
  z.object({ format: z.literal('paste'), text: z.string().max(100_000), locationId: LocationId.optional() }).strict(),
  z.object({ format: z.literal('csv'), uploadId: UploadId, locationId: LocationId.optional() }).strict(),
  z.object({ format: z.literal('photo'), uploadId: UploadId, locationId: LocationId.optional() }).strict(),
]);

export const BulkVerdict = z.enum(['ok', 'bad_number', 'landline', 'duplicate', 'opted_out', 'missing_contact', 'missing_name', 'over_12_months']);

export const BulkPreview = z.object({
  previewToken: z.string().regex(/^[A-Za-z0-9_-]{16,128}$/),
  expiresAt: IsoDateTime,
  rows: z
    .array(
      z.object({
        row: z.number().int().min(0),
        firstName: z.string().max(60).nullable(),
        contactLabel: z.string().max(80).nullable(),
        serviceText: z.string().max(80).nullable(),
        verdict: BulkVerdict,
        reason: z.string().max(160).nullable(),
      }),
    )
    .max(500),
  dailyCap: z.number().int().min(0),
});

export const BulkCommitBody = z
  .object({ previewToken: z.string().regex(/^[A-Za-z0-9_-]{16,128}$/), rows: z.array(z.number().int().min(0)).min(1).max(500), attestation: Attestation })
  .strict();

export const BulkCommitResponse = z.object({
  created: z.number().int().min(0),
  skipped: z.number().int().min(0),
  firstSendAt: IsoDateTime.nullable(),
});

// ---------------------------------------------------------------------------------------------- several at once (c133)

/**
 * Several asks at once, without a list ever being stored (c133, c132): the app reads the paste or the CSV in the
 * browser and sends rows. `check` answers a verdict per row and stores nothing; `send` checks every row again and
 * creates the asks. A CSV is for today's jobs only, so each of its rows carries the job's date. These replace the
 * bulk preview and commit above, which kept the list in a bucket between the two calls and now answer `gone`.
 */
export const AskEntry = z.enum(['paste', 'csv']);
export type AskEntry = z.infer<typeof AskEntry>;

export const AskRow = z
  .object({
    firstName: z.string().trim().max(60).optional(),
    /** As typed. The server normalises and judges it, so a bad address gets a verdict, never a 422 for the batch. */
    email: z.string().trim().min(1).max(254),
    /** The job's date as the file has it (CSV only): 2026-10-08, 10/8/2026 or 10/8/26. */
    jobDate: z.string().trim().max(40).optional(),
  })
  .strict();
export type AskRow = z.infer<typeof AskRow>;

export const AskVerdict = z.enum([
  'ok',
  'bad_email',
  /** Asked inside the repeat window, or a second copy in the same list. */
  'duplicate',
  'opted_out',
  /** The address bounced or complained before, for any business. Shown without saying which. */
  'undeliverable',
  'throwaway',
  /** noreply@, postmaster@ and the like: nobody reads it. */
  'no_reply_address',
  /** Someone on the business's own team: reviews from insiders break the FTC rule (docs/43 §4.1). */
  'own_team',
  /** A CSV row whose job wasn't today. */
  'not_today',
  /** A CSV row with no date we can read. */
  'no_date',
  /** Past today's limit, or past the size of one paste or import. */
  'over_limit',
]);
export type AskVerdict = z.infer<typeof AskVerdict>;

export const AskCheckBody = z
  .object({ entry: AskEntry, rows: z.array(AskRow).min(1).max(REVIEW_ASK_LIMITS.checkMax), locationId: LocationId.optional() })
  .strict();

export const AskRowVerdict = z.object({ row: z.number().int().min(0), verdict: AskVerdict, reason: z.string().max(160).nullable() });

export const AskCheck = z.object({
  rows: z.array(AskRowVerdict).max(REVIEW_ASK_LIMITS.checkMax),
  ok: z.number().int().min(0),
  /** Asks left today for this business. */
  leftToday: z.number().int().min(0),
  /** The business's date today, which a CSV row's job date must match. */
  today: z.iso.date(),
});

export const AskBatchBody = z
  .object({
    entry: AskEntry,
    rows: z.array(AskRow).min(1).max(REVIEW_ASK_LIMITS.importMax),
    locationId: LocationId.optional(),
    attestation: Attestation,
    idempotencyKey: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/),
  })
  .strict();

export const AskBatchResponse = z.object({
  created: z.number().int().min(0),
  /** Rows that didn't become an ask, each with why. Empty on a replayed send. */
  skipped: z.array(AskRowVerdict).max(REVIEW_ASK_LIMITS.importMax),
  firstSendAt: IsoDateTime.nullable(),
});

// ---------------------------------------------------------------------------------------------- the tracked review link and card (c133)

/** Card sizes: one for the van dash, one to leave behind with the invoice. */
export const ReviewCardSize = z.enum(['dash', 'leave_behind']);
export type ReviewCardSize = z.infer<typeof ReviewCardSize>;

export const ReviewCardQuery = z.object({ size: ReviewCardSize.optional(), location: LocationId.optional() }).strict();

export const TapCounts = z.object({ ask: z.number().int().min(0), link: z.number().int().min(0), card: z.number().int().min(0) });
export type TapCounts = z.infer<typeof TapCounts>;

const Hex = z.string().regex(/^#[0-9a-f]{6}$/).meta({ examples: ['#1f3d2b'] });

/**
 * The business's tracked review link (c133): a short link on our domain that always goes straight to their Google
 * review form, never through a sentiment step (docs/43 §4.2). An ask email carries the link with that ask's token.
 * The same link without a token, shared from the owner's own phone or scanned from the card, counts as a walk up.
 */
export const ReviewLinkView = z.object({
  /** False until we know the Google place ID. The link, the card and every ask wait for it. */
  ready: z.boolean(),
  url: HttpsUrl.nullable(),
  /** The words the Share button hands the phone, the link included. */
  shareText: z.string().max(400).nullable(),
  /** The card link's QR code for the on-screen card: a square of `size` modules, the dark ones as an SVG path. */
  qr: z.object({ size: z.number().int().min(21).max(177), path: z.string().max(60_000) }).nullable(),
  /** The card's colours, from the site's Design DNA. */
  brand: z.object({ ink: Hex, paper: Hex, accent: Hex }),
  businessName: z.string().max(60),
  /** The one line of copy on the card. */
  cardLine: z.string().max(80),
  cards: z.array(z.object({ size: ReviewCardSize, widthIn: z.number().positive(), heightIn: z.number().positive() })),
  taps: z.object({ last7Days: TapCounts, last30Days: TapCounts, latestAt: IsoDateTime.nullable() }),
  /** Asks sent by email in the last 7 days. */
  asksLast7Days: z.number().int().min(0),
});
export type ReviewLinkView = z.infer<typeof ReviewLinkView>;
